import type { LiveEvent } from '@setplex/tria-api'

import { StatusBadgeTone } from "/app/libraries/arcana/src/atoms/Label/Templates/index.ts"

import { EventStatus } from '@setplex/tria-api'
import {
  formatDateTimeWithYearUnified,
  formatTimeRangeUnified,
  formatTimeUnified,
} from '~/shared/helpers/date'

/**
 * Live / Past / Upcoming fields for the details `banner-description`.
 *
 * Validated Events frames: a live event shows the accent pill and a bare time
 * range; a finished event drops the pill and prefixes the date onto the
 * schedule line. Upcoming keeps the INFO pill and countdown from FP-4264.
 * There is no "Available until" copy.
 */
export const getLiveEventBannerInfo = (
  event: LiveEvent | null,
  liveTitle: string,
  upcomingTitle: string
) => {
  const live = event?.status === EventStatus.LIVE
  const upcoming =
    event?.status === EventStatus.SOON ||
    event?.status === EventStatus.LIVE_SOON
  const ended = event?.status === EventStatus.ENDED

  return {
    date: '',
    timeRange: scheduleLabel(event, upcoming || ended),
    countdownTo: upcoming && event?.startTime ? event.startTime : '',
    statusBadge: live ? liveTitle : upcoming ? upcomingTitle : '',
    statusBadgeTone: live
      ? StatusBadgeTone.ACCENT
      : upcoming
        ? StatusBadgeTone.INFO
        : StatusBadgeTone.ACCENT,
  }
}

/**
 * Player details always date-stamp via `formatLiveEventDetailsDateLabel`.
 * Live banners still need a time-only range, so the start-only fallback stays here.
 */
const scheduleLabel = (
  event: LiveEvent | null,
  includeDate: boolean
): string => {
  if (!event?.startTime) {
    return ''
  }

  if (event.endTime) {
    return formatTimeRangeUnified(event.startTime, event.endTime, {
      alwaysIncludeDate: includeDate,
    })
  }

  return includeDate
    ? formatDateTimeWithYearUnified(event.startTime)
    : formatTimeUnified(event.startTime)
}
